import React, { FunctionComponent, ReactElement } from 'react';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  Theme
} from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import jssPreset from '@mui/styles/jssPreset';
import { yellow, grey, orange, lightBlue } from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';
import { create } from 'jss';
import rtl from 'jss-rtl';

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

// A theme with custom primary and secondary color.
// It's optional.
export const theme = (dir?: 'ltr' | 'rtl'): Theme =>
  createTheme({
    direction: dir,
    palette: {
      contrastThreshold: 4.5, // WCAG 2 level AA requires a contrast ratio of at least 4.5:1 for normal text and 3:1 for large text.
      primary: {
        light: '#d7deff',
        main: '#3758fb',
        dark: '#1736a9'
      },
      secondary: {
        light: '#fdc5d7',
        main: '#e9055b',
        dark: '#91054d'
      },
      success: {
        light: '#d2f9ea',
        main: '#26d293',
        dark: '#008454',
        contrastText: '#333'
      },
      error: {
        light: '#fcd4d0',
        main: '#d9240f',
        dark: '#a71809'
      },
      warning: {
        light: '#fffad7',
        main: '#fbe637',
        dark: '#917205',
        contrastText: '#333'
      },
      information: {
        light: '#c6ecfc',
        main: '#37bbfb',
        dark: '#005ba4',
        contrastText: '#333',
        background: '#EBF8FE',
        pageBackground: '#FAFAFA'
      },
      idle: {
        light: '#fbece4',
        main: '#ff9966',
        dark: '#933100'
      },
      disable: {
        light: '#e0e0e0',
        main: '#616161',
        dark: '#212121'
      },
      excluded: {
        background: '#e9055b',
        contrastText: '#FFF'
      },
      table: {
        header: grey[200],
        body: 'rgba(0, 0, 0, 0.87)',
        border: 'rgba(0, 0, 0, 0.12)'
      },
      common: {
        highlight: yellow[50]
      },
      text: {
        primary: '#110434',
        secondary: 'rgba(0, 0, 0, 0.5)'
      },
      timesheetLabel: {
        container: '#ffffff',
        nonEmptylabel: '#3758fb',
        chipClickableColor: '#ffffff',
        customLabelChipColor: '#005ba4'
      },
      groupBy: {
        groupingTreeColor: grey[500],
        uncategorizedBorderColor: grey[500],
        groupByBackground: grey[200],
        uncategorizedBackground: '#EBF8FE',
        labelGroupHeaderBackground: '#FAFAFA'
      },
      timesheetLabelTable: {
        headerBackgroundColor: '#ffffff'
      },
      repliconBlue: {
        light: '#D9EBF7',
        main: '#1F5FF3',
        dark: '#00375A'
      },
      syncBackgroundStatus: {
        white: '#FFFFFF'
      },
      noteListIcon: {
        empty: orange[800]
      },
      notesLabel: {
        all: grey[300],
        inbox: '#c6ecfc',
        inboxHalfOpacity: 'rgba(215, 222, 255, 0.5)',
        private: '#fbe7d5',
        privateHalfOpacity: 'rgba(255, 224, 178, 0.5)',
        accepted: '#d2f9ea',
        acceptedHalfOpacity: 'rgba(220, 237, 200, 0.5)',
        trash: lightBlue[100],
        multiple: '#EDBAF8'
      },
      insights: {
        dateRangePickerDayBgColor: '#d7deff',
        noData: 'rgba(0, 0, 0, 0.11)',
        classificatonLabel: '#7d93fc'
      }
    },
    shadow: {
      paper: 'rgba(0,0,0,36%) 0px 0 12px 0px',
      menu: 'rgba(0,0,0,50%) 12px -12px 12px -15px',
      export: 'rgba(0,0,0,0.38) 1px 1px 5px 1px '
    },
    skelton: {
      color: '#bdbdbd'
    },
    typography: {
      useNextVariants: true,
      fontFamily: 'Roboto, Helvetica, Segoe UI, Arial, sans-serif',
      h1: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      h2: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      h3: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      h4: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      h5: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      h6: {
        fontFamily:
          '"Source Serif 4",serif,Poppins, Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      },
      body1: {
        fontFamily: 'Roboto, sans-serif,Poppins, Helvetica, Segoe UI, Arial'
      },
      body2: {
        fontFamily: 'Roboto,Poppins, Helvetica, Segoe UI, Arial, sans-serif'
      },
      subtitle1: {
        fontFamily: 'Roboto, Poppins, Helvetica, Segoe UI, Arial, sans-serif'
      },
      subtitle2: {
        fontFamily: 'Roboto, Poppins, Helvetica, Segoe UI, Arial, sans-serif'
      },
      button: {
        fontFamily: 'Roboto, sans-serif,Poppins, Helvetica, Segoe UI, Arial'
      },
      caption: {
        fontFamily: 'Roboto, Helvetica, Segoe UI, Arial, sans-serif'
      }
    },
    transition: {
      timing: '0.6s'
    },
    overrides: {
      MuiInputBase: {
        input: {
          fontFamily: 'Roboto, Helvetica, Segoe UI, Arial, sans-serif'
        }
      },
      MuiSelect: {
        selectMenu: {
          height: undefined
        }
      },
      MuiFormControlLabel: {
        label: {
          fontSize: '0.8125rem'
        }
      },
      MuiOutlinedInput: {
        root: {
          backgroundColor: '#fff'
        }
      },
      MuiCheckbox: {
        root: {
          width: 36,
          height: 36,
          color: 'rgba(0, 0, 0, 0.36)',
          '&:hover': {
            color: 'rgba(0, 0, 0, 1)'
          }
        }
      },
      MuiTooltip: {
        tooltip: {
          fontSize: 13
        }
      },
      MuiPickersBasePicker: {
        pickerView: { marginBottom: 16 }
      }
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: '#fdeded'
          }
        }
      }
    },
    padding: {
      input: '20px 12px 6px 12px'
    }
  } as any);

const MuiTheme: FunctionComponent<{
  direction: 'ltr' | 'rtl' | undefined;
  children: ReactElement;
  // eslint-disable-next-line react/prop-types
}> = ({ direction, children }) => {
  return (
    <StylesProvider jss={jss}>
      <CssBaseline />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme(direction)}>{children}</ThemeProvider>
      </StyledEngineProvider>
    </StylesProvider>
  );
};

export default MuiTheme;
